import { ref, computed, inject, watch, nextTick } from 'vue';
import { defineStore } from 'pinia';
import { useI18n } from 'vue-i18n';
import { onlineManager } from '@tanstack/vue-query';

export const STEP_ID = {
  BOOK_WITH_US: 'book-with-us',
  OCCASIONS: 'occasions',
  MENUS: 'menus',
  AREAS: 'areas',
  TIMESLOTS: 'timeslots',
  YOUR_DETAILS: 'your-details',
  DEPOSIT: 'deposit',
  CONFIRMATION: 'confirmation',
  ENQUIRY: 'enquiry',
  ENQUIRY_CONFIRMATION: 'enquiry-confirmation',
};

export const useBookingAppStore = defineStore('bookingApp', () => {
  const { t } = useI18n();

  const DIRECTION = {
    FORWARD: 'forward',
    BACKWARD: 'backward',
  };
  const JOURNEY_TYPE = {
    CORE: 'core',
    SPORTS: 'sports',
  };
  const VERIFICATION_RESULT_TYPES = {
    NO_NETWORK: 'no-network',
    NO_AVAILABLE_OCCASIONS: 'no-available-occasions',
    NO_AVAILABLE_AREAS: 'no-available-areas',
    NO_AVAILABLE_MENUS: 'no-available-menus',
    VERIFICATION_FAILED: 'verificationFailed',
    CAPACITY_RULES_FAILED: 'capacityRules',
    FULLY_BOOKED: 'fullyBooked',
  };
  const STEPS = [
    {
      id: STEP_ID.BOOK_WITH_US,
      label: 'Book with Us',
      isValid: false,
      completed: false,
      isLoading: false,
      isSkipped: false,
    },
    {
      id: STEP_ID.OCCASIONS,
      label: 'Occasions',
      isValid: false,
      completed: false,
      isLoading: false,
      isSkipped: false,
    },
    {
      id: STEP_ID.MENUS,
      label: 'Menus',
      isValid: false,
      completed: false,
      isLoading: false,
      isSkipped: false,
    },
    {
      id: STEP_ID.AREAS,
      label: 'Areas',
      isValid: false,
      completed: false,
      isLoading: false,
      isSkipped: false,
    },
    {
      id: STEP_ID.TIMESLOTS,
      label: 'Timeslots',
      isValid: false,
      completed: false,
      isLoading: false,
      isSkipped: false,
    },
    {
      id: STEP_ID.YOUR_DETAILS,
      label: 'Your Details',
      isValid: false,
      completed: false,
      isLoading: false,
      isSkipped: false,
    },
    {
      id: STEP_ID.DEPOSIT,
      label: 'Pay deposit',
      isValid: false,
      completed: false,
      isLoading: false,
      isSkipped: false,
    },
    {
      id: STEP_ID.CONFIRMATION,
      label: 'Confirmation',
      isValid: false,
      completed: false,
      isLoading: false,
      isSkipped: false,
    },
    {
      id: STEP_ID.ENQUIRY,
      label: 'Enquiry',
      isValid: false,
      completed: false,
      isLoading: false,
      isSkipped: true,
    },
    {
      id: STEP_ID.ENQUIRY_CONFIRMATION,
      label: 'Confirmation',
      isValid: false,
      completed: false,
      isLoading: false,
      isSkipped: true,
    },
  ];

  const SKIPPED_STEP_CLEARING_ORDER = [
    STEP_ID.OCCASIONS,
    STEP_ID.MENUS,
    STEP_ID.AREAS,
    STEP_ID.TIMESLOTS,
  ];

  const DEFAULT_TIMER_DURATION = 600;

  const DEFAULT_STATE = {
    apiUrl: '',
    currentStepId: STEP_ID.BOOK_WITH_US,
    stepBeforeEnquiry: STEP_ID.OCCASIONS,
    isSummaryVisible: false,
    isLoading: false,
    isQuickBookLoading: false,
    isQuickBookDataApplied: false,
    isQuickBookJourney: false,
    mobileApp: false,
    adults: 2,
    enquiryTimeslotsAdults: 2,
    children: 0,
    date: null,
    sportId: '',
    inPubEventId: '',
    occasionFromQuickbook: '',
    firstName: '',
    lastName: '',
    emailAddress: '',
    telephoneNumber: '',
    venueDetails: {
      siteId: '',
      termsAndConditionsUrl: '',
      maxCovers: 0,
      minCovers: 0,
      isWetLed: false,
      phoneNumber: '',
      disabledDates: [],
      disabledWeekDays: [],
    },
    competition: null,
    timeslots: [],
    timeslotsDetailed: [],
    occasions: [],
    menus: [],
    areas: [],
    createdEvent: null,
    createdEnquiry: null,
    wheelchairAccess: '',
    specialRequest: '',
    highchairs: 0,
    requirements: {},
    competitionOptIn: false,
    menuTermsOptIn: false,
    enquiryFormEnabled: false,
    fullyBooked: false,
    selectedOccasion: '',
    selectedMenu: '',
    selectedArea: '',
    selectedTimeslot: '',
    notificationModalType: 'custom',
    notificationModalContactAvailable: true,
    verificationMessage: '',
    verifyOccasionData: {},
    skipIntervalId: null,
    consentStatement: null,
    startTimestamp: null,
    timeIsUp: false,
    timer: DEFAULT_TIMER_DURATION,
    payment: {},
    updatedPayment: {},
    journeyType: JOURNEY_TYPE.CORE,
    initialJourneyType: null,
    initialSelectedAreaName: '',
    checkoutCount: 1,
    beginCheckoutTimestamp: null,
    useTimeslotsDetailed: true,
    venueFinderLink: null,
  };

  const apiUrl = ref(DEFAULT_STATE.apiUrl);
  const currentStepId = ref(DEFAULT_STATE.currentStepId);
  const stepBeforeEnquiry = ref(DEFAULT_STATE.stepBeforeEnquiry);
  const steps = ref(STEPS);
  const isSummaryVisible = ref(DEFAULT_STATE.isSummaryVisible);
  const isLoading = ref(DEFAULT_STATE.isLoading);
  const isQuickBookLoading = ref(DEFAULT_STATE.isQuickBookLoading);
  const isQuickBookDataApplied = ref(DEFAULT_STATE.isQuickBookDataApplied);
  const isQuickBookJourney = ref(DEFAULT_STATE.isQuickBookJourney);
  const mobileApp = ref(DEFAULT_STATE.mobileApp);
  const adults = ref(DEFAULT_STATE.adults);
  const enquiryTimeslotsAdults = ref(DEFAULT_STATE.enquiryTimeslotsAdults);
  const children = ref(DEFAULT_STATE.children);
  const date = ref(DEFAULT_STATE.date);
  const sportId = ref(DEFAULT_STATE.sportId);
  const inPubEventId = ref(DEFAULT_STATE.inPubEventId);
  const occasionFromQuickbook = ref(DEFAULT_STATE.occasionFromQuickbook);
  const firstName = ref(DEFAULT_STATE.firstName);
  const lastName = ref(DEFAULT_STATE.lastName);
  const emailAddress = ref(DEFAULT_STATE.emailAddress);
  const confirmationEmail = ref(DEFAULT_STATE.emailAddress);
  const telephoneNumber = ref(DEFAULT_STATE.telephoneNumber);
  const venueDetails = ref(DEFAULT_STATE.venueDetails);
  const competition = ref(DEFAULT_STATE.competition);
  const timeslots = ref(DEFAULT_STATE.timeslots);
  const timeslotsDetailed = ref(DEFAULT_STATE.timeslotsDetailed);
  const occasions = ref(DEFAULT_STATE.occasions);
  const menus = ref(DEFAULT_STATE.menus);
  const areas = ref(DEFAULT_STATE.areas);
  const createdEvent = ref(DEFAULT_STATE.createdEvent);
  const createdEnquiry = ref(DEFAULT_STATE.createdEnquiry);
  const wheelchairAccess = ref(DEFAULT_STATE.wheelchairAccess);
  const specialRequest = ref(DEFAULT_STATE.specialRequest);
  const highchairs = ref(DEFAULT_STATE.highchairs);
  const competitionOptIn = ref(DEFAULT_STATE.competitionOptIn);
  const menuTermsOptIn = ref(DEFAULT_STATE.menuTermsOptIn);
  const enquiryFormEnabled = ref(DEFAULT_STATE.enquiryFormEnabled);
  const fullyBooked = ref(DEFAULT_STATE.fullyBooked);
  const selectedOccasion = ref(DEFAULT_STATE.selectedOccasion);
  const selectedMenu = ref(DEFAULT_STATE.selectedMenu);
  const selectedArea = ref(DEFAULT_STATE.selectedArea);
  const selectedTimeslot = ref(DEFAULT_STATE.selectedTimeslot);
  const notificationModalType = ref(DEFAULT_STATE.notificationModalType);
  const notificationModalContactAvailable = ref(DEFAULT_STATE.notificationModalContactAvailable);
  const verificationMessage = ref(DEFAULT_STATE.verificationMessage);
  const unavailableModalRef = ref(null);
  const verifyOccasionData = ref(DEFAULT_STATE.verifyOccasionData);
  const skipIntervalId = ref(DEFAULT_STATE.skipIntervalId);
  const consentStatement = ref(DEFAULT_STATE.consentStatement);
  const requirements = ref(DEFAULT_STATE.requirements);
  const timer = ref(DEFAULT_TIMER_DURATION);
  const timeIsUp = ref(false);
  const startTimestamp = ref(null);
  const payment = ref(DEFAULT_STATE.payment);
  const updatedPayment = ref(DEFAULT_STATE.updatedPayment);
  const journeyType = ref(DEFAULT_STATE.journeyType);
  const initialJourneyType = ref(DEFAULT_STATE.initialJourneyType);
  const initialSelectedAreaName = ref(DEFAULT_STATE.initialSelectedAreaName);
  const checkoutCount = ref(DEFAULT_STATE.checkoutCount);
  const bookingEntryPage = ref('unknown');
  const beginCheckoutTimestamp = ref(DEFAULT_STATE.beginCheckoutTimestamp);
  const useTimeslotsDetailed = ref(DEFAULT_STATE.useTimeslotsDetailed);
  const venueFinderLink = ref(DEFAULT_STATE.venueFinderLink);

  let timerInterval = null;

  const jssStore = inject('jssStore');

  const venueId = computed(() => jssStore?.sitecoreContext()?.venueId?.toString().padStart(4, '0'));

  const getCurrentStep = computed(() => {
    return steps.value.find((step) => step.id === currentStepId.value);
  });

  const getStep = computed(() => {
    return (stepId) => {
      return steps.value.find((step) => step.id === stepId);
    };
  });

  const getCurrentStepIndex = computed(() => {
    return steps.value.findIndex((step) => step.id === currentStepId.value);
  });

  const numberOfCovers = computed(() => +adults.value + +children.value);

  const isDepositRequired = computed(() => {
    return selectedMenuItem.value?.depositTotal > 0;
  });

  const bookingData = computed(() => ({
    adults: adults.value,
    areaId: selectedArea.value,
    children: children.value,
    competition: competitionOptIn.value,
    consentStatement: consentStatement.value,
    consentMenuTerms: menuTermsOptIn.value,
    consentTerms: true,
    date: date.value,
    emailAddress: emailAddress.value,
    firstName: firstName.value,
    lastName: lastName.value,
    menus: [selectedMenu.value].filter(Boolean),
    occasionId: selectedOccasion.value,
    siteId: venueDetails.value?.siteId,
    specialRequest: [
      wheelchairAccess.value,
      highchairs.value && `Highchairs: ${highchairs.value}`,
      specialRequest.value,
    ]
      .filter(Boolean)
      .join(', '),
    telephoneNumber: telephoneNumber.value,
    time: selectedTimeslot.value,
    upsells: [],
    fixtureName: selectedOccasionItem.value?.fixtureDetails?.name || '',
  }));

  const $reset = () => {
    apiUrl.value = DEFAULT_STATE.apiUrl;
    currentStepId.value = DEFAULT_STATE.currentStepId;
    stepBeforeEnquiry.value = DEFAULT_STATE.stepBeforeEnquiry;
    isSummaryVisible.value = DEFAULT_STATE.isSummaryVisible;
    isLoading.value = DEFAULT_STATE.isLoading;
    isQuickBookLoading.value = DEFAULT_STATE.isQuickBookLoading;
    isQuickBookDataApplied.value = DEFAULT_STATE.isQuickBookDataApplied;
    isQuickBookJourney.value = DEFAULT_STATE.isQuickBookJourney;
    venueDetails.value = { ...DEFAULT_STATE.venueDetails };
    competition.value = DEFAULT_STATE.competition;
    adults.value = DEFAULT_STATE.adults;
    enquiryTimeslotsAdults.value = DEFAULT_STATE.enquiryTimeslotsAdults;
    children.value = DEFAULT_STATE.children;
    date.value = DEFAULT_STATE.date;
    firstName.value = DEFAULT_STATE.firstName;
    lastName.value = DEFAULT_STATE.lastName;
    emailAddress.value = DEFAULT_STATE.emailAddress;
    confirmationEmail.value = DEFAULT_STATE.emailAddress;
    telephoneNumber.value = DEFAULT_STATE.telephoneNumber;
    steps.value = [...STEPS];
    timeslots.value = DEFAULT_STATE.timeslots;
    timeslotsDetailed.value = DEFAULT_STATE.timeslotsDetailed;
    occasions.value = DEFAULT_STATE.occasions;
    menus.value = DEFAULT_STATE.menus;
    areas.value = DEFAULT_STATE.areas;
    createdEvent.value = DEFAULT_STATE.createdEvent;
    createdEnquiry.value = DEFAULT_STATE.createdEnquiry;
    wheelchairAccess.value = DEFAULT_STATE.wheelchairAccess;
    specialRequest.value = DEFAULT_STATE.specialRequest;
    highchairs.value = DEFAULT_STATE.highchairs;
    competitionOptIn.value = DEFAULT_STATE.competitionOptIn;
    menuTermsOptIn.value = DEFAULT_STATE.menuTermsOptIn;
    enquiryFormEnabled.value = DEFAULT_STATE.enquiryFormEnabled;
    fullyBooked.value = DEFAULT_STATE.fullyBooked;
    verifyOccasionData.value = DEFAULT_STATE.verifyOccasionData;
    skipIntervalId.value = DEFAULT_STATE.skipIntervalId;
    consentStatement.value = DEFAULT_STATE.consentStatement;
    startTimestamp.value = null;
    timeIsUp.value = false;
    timer.value = DEFAULT_TIMER_DURATION;
    payment.value = DEFAULT_STATE.payment;
    updatedPayment.value = DEFAULT_STATE.updatedPayment;
    journeyType.value = DEFAULT_STATE.journeyType;
    initialJourneyType.value = DEFAULT_STATE.initialJourneyType;
    initialSelectedAreaName.value = DEFAULT_STATE.initialSelectedAreaName;
    checkoutCount.value = DEFAULT_STATE.checkoutCount;
    beginCheckoutTimestamp.value = DEFAULT_STATE.beginCheckoutTimestamp;
    mobileApp.value = DEFAULT_STATE.mobileApp;
    sportId.value = DEFAULT_STATE.sportId;
    inPubEventId.value = DEFAULT_STATE.inPubEventId;
    occasionFromQuickbook.value = DEFAULT_STATE.occasionFromQuickbook;
    useTimeslotsDetailed.value = DEFAULT_STATE.useTimeslotsDetailed;
  };

  const resetOnSportJourney = () => {
    adults.value = DEFAULT_STATE.adults;
    children.value = DEFAULT_STATE.children;
    date.value = DEFAULT_STATE.date;
    journeyType.value = DEFAULT_STATE.journeyType;
    sportId.value = DEFAULT_STATE.sportId;
    inPubEventId.value = DEFAULT_STATE.inPubEventId;
    occasionFromQuickbook.value = DEFAULT_STATE.occasionFromQuickbook;
  };

  const setCurrentStep = (step) => {
    const stepIndex = steps.value.findIndex((s) => s.id === step);
    if (stepIndex !== -1) {
      currentStepId.value = step;
      steps.value = steps.value.map((s, index) => ({
        ...s,
        completed: index < stepIndex,
      }));
    }
  };

  const setStepBeforeEnquiry = (step) => {
    stepBeforeEnquiry.value = step;
  };

  const setStepValid = (stepId, isValid) => {
    steps.value = steps.value.map((step) => {
      const isTargetStep = step.id === stepId;
      return isTargetStep ? { ...step, isValid } : step;
    });
  };

  const setStepLoading = (stepId, isLoading) => {
    steps.value = steps.value.map((step) => {
      const isTargetStep = step.id === stepId;
      return isTargetStep ? { ...step, isLoading } : step;
    });
  };

  const setStepSkipped = (stepId, isSkipped) => {
    steps.value = steps.value.map((step) => {
      const isTargetStep = step.id === stepId;
      return isTargetStep ? { ...step, isSkipped } : step;
    });
  };

  const isCurrentStepValid = computed(() => {
    const step = steps.value.find((s) => s.id === currentStepId.value);
    return step ? step.isValid : true;
  });

  const availableOccasions = computed(() =>
    occasions.value.filter((occasion) => occasion.available)
  );
  const availableMenus = computed(() => menus.value.filter((menu) => menu.available));
  const availableAreas = computed(() => areas.value.filter((area) => area.available));
  const availableTimeslots = computed(() =>
    timeslots.value.filter((timeslot) => timeslot.available)
  );

  const isSportsJourney = computed(() => journeyType.value === JOURNEY_TYPE.SPORTS);

  const skipConditions = {
    occasions: () => availableOccasions.value.length === 1,
    menus: () => availableMenus.value.length === 1 && !availableMenus.value[0].depositTotal,
    areas: () => areas.value.length === 1 && availableAreas.value.length === 1,
    deposit: () => !isDepositRequired.value,
  };

  const clearSkippedFrom = (stepId) => {
    const startIdx = SKIPPED_STEP_CLEARING_ORDER.indexOf(stepId);
    if (startIdx !== -1) {
      SKIPPED_STEP_CLEARING_ORDER.slice(startIdx + 1).forEach((id) => {
        setStepSkipped(id, false);
      });
    }
  };

  const skipActions = {
    occasions: () => {
      if (skipConditions.occasions()) {
        selectedOccasion.value = availableOccasions.value[0]?.id;
        setStepSkipped(STEP_ID.OCCASIONS, true);
      }
    },
    menus: () => {
      if (skipConditions.menus()) {
        selectedMenu.value = availableMenus.value[0]?.id;
        setStepSkipped(STEP_ID.MENUS, true);
      }
    },
    areas: () => {
      if (skipConditions.areas()) {
        selectedArea.value = availableAreas.value[0]?.id;
        setStepSkipped(STEP_ID.AREAS, true);
      }
    },
    deposit: () => {
      if (skipConditions.deposit()) {
        setStepSkipped(STEP_ID.DEPOSIT, true);
      }
    },
  };

  const shouldSkipStep = (stepId) => {
    return skipConditions[stepId]?.() || false;
  };

  const stepChecks = {
    [STEP_ID.BOOK_WITH_US]: async () => {
      await waitForStepLoadingComplete(steps.value.findIndex((s) => s.id === STEP_ID.OCCASIONS));
      if (!availableOccasions.value.length) {
        return { type: VERIFICATION_RESULT_TYPES.NO_AVAILABLE_OCCASIONS, success: false };
      } else if (fullyBooked.value) {
        return { type: VERIFICATION_RESULT_TYPES.FULLY_BOOKED, success: false };
      }
      return { success: true };
    },
    [STEP_ID.OCCASIONS]: async () => {
      const unavailabilityRulesResult = checkUnavailabilityRules();
      if (!unavailabilityRulesResult.success) return unavailabilityRulesResult;

      await waitForStepLoadingComplete(steps.value.findIndex((s) => s.id === STEP_ID.AREAS));
      if (!availableAreas.value.length) {
        return { type: VERIFICATION_RESULT_TYPES.NO_AVAILABLE_AREAS, success: false };
      }

      await waitForStepLoadingComplete(steps.value.findIndex((s) => s.id === STEP_ID.MENUS));
      if (!availableMenus.value.length) {
        return { type: VERIFICATION_RESULT_TYPES.NO_AVAILABLE_MENUS, success: false };
      }

      return { success: true };
    },
  };

  const waitForStepLoadingComplete = (index) => {
    return new Promise((resolve) => {
      if (!steps.value[index].isLoading) {
        return resolve();
      }

      if (skipIntervalId.value) {
        clearInterval(skipIntervalId.value);
        skipIntervalId.value = DEFAULT_STATE.skipIntervalId;
      }

      const id = setInterval(() => {
        const step = steps.value[index];
        if (!step.isLoading) {
          clearInterval(id);
          skipIntervalId.value = DEFAULT_STATE.skipIntervalId;
          resolve();
        }
      }, 100);

      skipIntervalId.value = id;
    });
  };

  const cancelWaitForStepLoadingComplete = () => {
    if (skipIntervalId.value) {
      clearInterval(skipIntervalId.value);
      skipIntervalId.value = DEFAULT_STATE.skipIntervalId;
    }
  };

  const navigateWithSkip = async (direction, index) => {
    if (index === -1) {
      setCurrentStep(STEP_ID.BOOK_WITH_US);
      return { success: true };
    }

    const nextIndex = direction === DIRECTION.FORWARD ? index + 1 : index - 1;

    if (nextIndex < 0 || nextIndex >= steps.value.length)
      return { success: false, type: 'out-of-range' };

    isLoading.value = true;

    if (direction === DIRECTION.FORWARD) {
      if (!onlineManager.isOnline()) {
        isLoading.value = false;
        return { success: false, type: VERIFICATION_RESULT_TYPES.NO_NETWORK };
      }

      const stepChecksResult = await stepChecks[steps.value[index].id]?.();
      if (stepChecksResult && !stepChecksResult.success) {
        isLoading.value = false;
        return stepChecksResult;
      }
    }

    await waitForStepLoadingComplete(nextIndex);

    if (shouldSkipStep(steps.value[nextIndex].id)) {
      skipActions[steps.value[nextIndex].id]?.(direction);
      await nextTick();
      return await navigateWithSkip(direction, nextIndex);
    }

    setCurrentStep(steps.value[nextIndex].id);
    isLoading.value = false;
    return { success: true };
  };

  const goToNextStepWithSkip = () => {
    if (isLoading.value) return Promise.resolve({ success: false, type: 'is-loading' });
    return navigateWithSkip(DIRECTION.FORWARD, getCurrentStepIndex.value);
  };

  const goToPreviousStepWithSkip = () => {
    if (skipIntervalId.value) {
      cancelWaitForStepLoadingComplete();
      isLoading.value = false;
      return Promise.resolve({ success: true, type: 'cancelled' });
    }
    if (currentStepId.value === STEP_ID.ENQUIRY) {
      setCurrentStep(stepBeforeEnquiry.value);
      return Promise.resolve({ success: true });
    }
    return navigateWithSkip(DIRECTION.BACKWARD, getCurrentStepIndex.value);
  };

  const isBeforeStep = (stepId) => {
    const stepIndex = steps.value.findIndex((step) => step.id === stepId);
    return getCurrentStepIndex.value < stepIndex;
  };

  const calculateTimeLeft = () => {
    const elapsedTime = Math.floor((Date.now() - startTimestamp.value) / 1000);
    return Math.max(DEFAULT_TIMER_DURATION - elapsedTime, 0);
  };

  const startTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
    }
    startTimestamp.value = Date.now();
    timeIsUp.value = false;

    timer.value = calculateTimeLeft();

    timerInterval = setInterval(() => {
      timer.value = calculateTimeLeft();

      if (timer.value === 0) {
        clearInterval(timerInterval);
        handleTimerEnd();
      }
    }, 1000);
  };

  const resetTimer = (newTime = DEFAULT_TIMER_DURATION) => {
    startTimestamp.value = Date.now() - (DEFAULT_TIMER_DURATION - newTime) * 1000;
    timer.value = newTime;
    timeIsUp.value = false;
  };

  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
  };

  const handleTimerEnd = () => {
    timeIsUp.value = true;
  };

  const selectedMenuItem = computed(() =>
    menus.value.find((menu) => menu.id === selectedMenu.value)
  );

  const selectedAreaItem = computed(() =>
    areas.value.find((area) => area.id === selectedArea.value)
  );

  const selectedOccasionItem = computed(() =>
    occasions.value.find((occasion) => occasion.id === selectedOccasion.value)
  );

  const selectedTimeslotItem = computed(() =>
    timeslots.value.find((timeslot) => timeslot.time === selectedTimeslot.value)
  );

  const shouldShowEnquiryForm = computed(() => {
    if (selectedOccasion.value && areas.value.length === 0) {
      return false;
    }

    return enquiryFormEnabled.value;
  });

  const canEnquireTimeslots = computed(() => {
    return timeslotsDetailed.value.some((timeSlot) =>
      timeSlot.availabilities.some((availability) => availability.canEnquire === true)
    );
  });

  const checkCapacityRules = (minCovers, maxCovers) => {
    if (!minCovers && !maxCovers) {
      return false;
    }

    if (minCovers && numberOfCovers.value < minCovers) {
      verificationMessage.value = [t('booking-app-msg-min-covers', { minCovers: minCovers })];
      return true;
    }

    if (maxCovers && numberOfCovers.value > maxCovers) {
      verificationMessage.value = [t('booking-app-msg-max-covers', { maxCovers: maxCovers })];
      return true;
    }

    return false;
  };

  const checkUnavailabilityRules = () => {
    const verificationMessages = verifyOccasionData.value?.messages;

    if (verificationMessages?.length > 0) {
      selectedOccasion.value = DEFAULT_STATE.selectedOccasion;
      return {
        success: false,
        type: VERIFICATION_RESULT_TYPES.VERIFICATION_FAILED,
        message: verificationMessages[0],
        contactAvailable: verifyOccasionData.value?.contactAvailable,
      };
    }

    if (
      checkCapacityRules(
        selectedOccasionItem.value?.minimumCovers,
        selectedOccasionItem.value?.maximumCovers
      )
    ) {
      return {
        success: false,
        type: VERIFICATION_RESULT_TYPES.CAPACITY_RULES_FAILED,
        message: verificationMessage.value[0],
      };
    }

    return { success: true, type: 'success' };
  };

  const setJourneyType = (type) => {
    journeyType.value = type;
    initialJourneyType.value ??= type;
  };

  const increaseCheckoutCount = () => {
    checkoutCount.value += 1;
  };

  const setBookingEntryPage = (page) => {
    bookingEntryPage.value = page || 'unknown';
  };

  watch(availableAreas, (newAvailableAreas) => {
    if (selectedArea.value && !newAvailableAreas.some((area) => area.id === selectedArea.value)) {
      selectedArea.value = DEFAULT_STATE.selectedArea;
    }
  });

  watch(availableMenus, (newAvailableMenus) => {
    if (selectedMenu.value && !newAvailableMenus.some((menu) => menu.id === selectedMenu.value)) {
      selectedMenu.value = DEFAULT_STATE.selectedMenu;
    }
  });

  watch(availableOccasions, (newAvailableOccasions) => {
    if (
      selectedOccasion.value &&
      !newAvailableOccasions.some((occasion) => occasion.id === selectedOccasion.value)
    ) {
      selectedOccasion.value = DEFAULT_STATE.selectedOccasion;
    }
  });

  watch(availableTimeslots, (newAvailableTimeslots) => {
    if (
      selectedTimeslot.value &&
      !newAvailableTimeslots.some((timeslot) => timeslot.time === selectedTimeslot.value)
    ) {
      selectedTimeslot.value = DEFAULT_STATE.selectedTimeslot;
    }
  });

  watch(selectedOccasion, (newValue) => {
    if (currentStepId.value !== STEP_ID.ENQUIRY) {
      setStepValid(STEP_ID.OCCASIONS, !!newValue);
    }
    clearSkippedFrom(STEP_ID.OCCASIONS);
  });

  watch(selectedMenu, (newValue) => {
    if (currentStepId.value !== STEP_ID.ENQUIRY) {
      setStepValid(STEP_ID.MENUS, !!newValue);
    }
    menuTermsOptIn.value = DEFAULT_STATE.menuTermsOptIn;
    clearSkippedFrom(STEP_ID.MENUS);
  });

  watch(selectedArea, (newValue) => {
    if (!initialSelectedAreaName.value) {
      initialSelectedAreaName.value = selectedAreaItem.value?.name;
    }
    if (currentStepId.value !== STEP_ID.ENQUIRY) {
      setStepValid(STEP_ID.AREAS, !!newValue);
    }
    clearSkippedFrom(STEP_ID.AREAS);
  });

  watch(selectedTimeslot, (newValue) => {
    if (currentStepId.value !== STEP_ID.ENQUIRY) {
      setStepValid(STEP_ID.TIMESLOTS, !!newValue);
    }
    clearSkippedFrom(STEP_ID.TIMESLOTS);
  });

  watch([selectedArea, timeslotsDetailed], () => {
    if (!selectedArea.value || !timeslotsDetailed.value.length) return;

    timeslots.value =
      timeslotsDetailed.value.reduce((acc, timeslot) => {
        const availability = timeslot.availabilities.find(
          (avail) => avail.areaId === selectedArea.value
        );
        if (availability) {
          acc.push({
            time: timeslot.time,
            group: timeslot.group,
            ...availability,
          });
        }
        return acc;
      }, []) || [];
  });

  return {
    STEP_ID,
    JOURNEY_TYPE,
    VERIFICATION_RESULT_TYPES,
    apiUrl,
    currentStepId,
    stepBeforeEnquiry,
    steps,
    isSummaryVisible,
    isLoading,
    isQuickBookLoading,
    mobileApp,
    adults,
    enquiryTimeslotsAdults,
    children,
    date,
    sportId,
    inPubEventId,
    occasionFromQuickbook,
    firstName,
    lastName,
    emailAddress,
    confirmationEmail,
    telephoneNumber,
    specialRequest,
    wheelchairAccess,
    highchairs,
    competitionOptIn,
    menuTermsOptIn,
    selectedOccasion,
    selectedMenu,
    selectedArea,
    selectedTimeslot,
    skipIntervalId,
    consentStatement,
    bookingEntryPage,
    beginCheckoutTimestamp,
    useTimeslotsDetailed,
    // Computed properties
    availableOccasions,
    availableMenus,
    availableAreas,
    selectedMenuItem,
    selectedAreaItem,
    selectedOccasionItem,
    selectedTimeslotItem,
    // API Data
    venueDetails,
    venueFinderLink,
    competition,
    occasions,
    menus,
    timeslots,
    timeslotsDetailed,
    areas,
    createdEvent,
    createdEnquiry,
    requirements,
    verifyOccasionData,
    // Computed
    venueId,
    getCurrentStep,
    bookingData,
    getStep,
    isCurrentStepValid,
    isDepositRequired,
    numberOfCovers,
    enquiryFormEnabled,
    shouldShowEnquiryForm,
    canEnquireTimeslots,
    fullyBooked,
    notificationModalType,
    notificationModalContactAvailable,
    verificationMessage,
    unavailableModalRef,
    // Methods
    $reset,
    resetOnSportJourney,
    setCurrentStep,
    setStepBeforeEnquiry,
    setStepLoading,
    goToNextStep: goToNextStepWithSkip,
    goToPreviousStep: goToPreviousStepWithSkip,
    setStepValid,
    shouldSkipStep,
    skipConditions,
    isBeforeStep,
    getCurrentStepIndex,
    checkUnavailabilityRules,
    setStepSkipped,
    setBookingEntryPage,
    checkCapacityRules,
    //timer
    timer,
    startTimer,
    resetTimer,
    timeIsUp,
    stopTimer,
    calculateTimeLeft,
    //payment
    payment,
    updatedPayment,
    //DataLayer
    journeyType,
    isQuickBookDataApplied,
    isQuickBookJourney,
    isSportsJourney,
    initialJourneyType,
    initialSelectedAreaName,
    setJourneyType,
    checkoutCount,
    increaseCheckoutCount,
  };
});
